import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Navigate, useNavigate, useParams, useSearchParams} from 'react-router-dom';

import logo from '../../images/logo_dashbo_purple.svg';
import {useFicha21} from '../../context/UserModulesContext';
import {Spinner} from '../Spinner';
import {SignOut} from '../icons';
import {Stepper} from '../onboarding/Stepper';
import {clearOauthReturn} from '../onboarding/oauthReturn';
import {
    clearDraft, effectiveClientId, lineWizardEntryPath, loadDraft, newDraft, reachableStep, saveDraft, visibleSteps,
} from './lineWizardDraft';
import '../onboarding/onboarding.scss';
import './lineWizard.scss';

/**
 * El marco del wizard «Nueva línea de control» (card CvdQbnl2).
 *
 * Usa el lenguaje del túnel de alta —pantalla completa, clara, sin el menú de la aplicación— porque
 * así lo decidió el mockup (`ux21-onboarding-flujo.html`, decisión 8): los tres caminos para crear
 * una línea hablan el mismo idioma visual. No monta el `OnboardingProvider` del túnel: este wizard
 * tiene su propio borrador y no toca el del alta.
 *
 * Cada paso es su propia ruta (`/lines/new/<paso>`) por el mismo motivo que en el túnel: «Vincular
 * más cuentas» autoriza navegando fuera de la aplicación y tiene que poder volver al paso 3.
 */

const DraftContext = createContext(null);

export function useLineWizard() {
    return useContext(DraftContext);
}

export function wizardPath(step) {
    return `/lines/new/${step}`;
}

/**
 * La espera del marco (módulos del usuario, borrador): el fondo del wizard con el spinner, nunca
 * `null`. Una pantalla en blanco sin nada que se mueva se lee como «se rompió» (QA del 25/09).
 */
function WizardLoading() {
    return (
        <div className="ob-tunnel lw-tunnel">
            <div className="box-loader lw-loading"><Spinner color="positive"/></div>
        </div>
    );
}

/**
 * La entrada: `/lines/new?client=<id>`. Siempre arranca un borrador NUEVO — apretar «Nueva línea de
 * seguimiento» es empezar una línea, no retomar la anterior — y lleva al primer paso que el usuario
 * tiene que hacer: «La línea» si vino desde un cliente, «Cliente» si no.
 */
export function LineWizardEntry() {
    const [params] = useSearchParams();
    const {ficha21, loaded} = useFicha21();
    const clientId = params.get('client');
    const [firstStep, setFirstStep] = useState(null);

    useEffect(() => {
        if (!loaded || !ficha21) return;
        const draft = newDraft({clientId});
        clearDraft();
        saveDraft(draft);
        setFirstStep(visibleSteps(draft)[0]);
    }, [loaded, ficha21, clientId]);

    if (!loaded) return <WizardLoading/>;
    // Sin la 2.1 no hay wizard: la línea se crea con el flujo clásico, desde el cliente.
    if (!ficha21) return <Navigate to={clientId ? `/client/${clientId}` : '/agency/clients'} replace/>;
    if (!firstStep) return <WizardLoading/>;
    return <Navigate to={wizardPath(firstStep)} replace/>;
}

export function LineWizardScreen({children}) {
    const {t} = useTranslation();
    const {step} = useParams();
    const navigate = useNavigate();
    const {ficha21, loaded} = useFicha21();
    const [draft, setDraft] = useState(() => loadDraft());
    const draftRef = useRef(draft);

    // La nota de «volver del OAuth» no sobrevive al wizard: lo mismo que hace el marco del túnel.
    useEffect(() => clearOauthReturn, []);

    const update = useCallback((changer) => {
        const next = typeof changer === 'function' ? changer(draftRef.current) : changer;
        draftRef.current = next;
        saveDraft(next);
        setDraft(next);
        return next;
    }, []);

    const leave = useCallback(() => {
        const clientId = draftRef.current ? effectiveClientId(draftRef.current) : null;
        clearOauthReturn();
        clearDraft();
        navigate(clientId ? `/client/${clientId}` : '/agency/clients');
    }, [navigate]);

    if (!loaded) return <WizardLoading/>;
    if (!ficha21) return <Navigate to="/agency/clients" replace/>;
    // Sin borrador (URL directa, otra pestaña: el borrador vive en la pestaña) se vuelve a empezar
    // desde el paso 1 con uno nuevo: los pasos siguientes no tienen con qué trabajar.
    if (!draft) return <Navigate to={lineWizardEntryPath(null)} replace/>;
    // Un paso que este borrador no muestra (el «Cliente» de una entrada desde un cliente, una URL
    // inventada) va al primero que sí: dibujarlo dejaba el stepper sin paso actual, todo tildado.
    const steps = visibleSteps(draft);
    if (!steps.includes(step)) return <Navigate to={wizardPath(steps[0])} replace/>;
    // Con borrador, pero sin lo que este paso necesita: al primero que quedó sin completar.
    const reachable = reachableStep(draft, step);
    if (reachable !== step) return <Navigate to={wizardPath(reachable)} replace/>;

    return (
        <DraftContext.Provider value={{draft, update, leave, step}}>
            <div className="ob-tunnel lw-tunnel">
                <header className="ob-topbar">
                    <span className="lw-brand">
                        <img className="ob-topbar__logo" src={logo} alt=""/>
                        <span className="lw-brand__name">Dashbo</span>
                    </span>
                    <Stepper current={step} steps={steps} labelPrefix="line_wizard.stepper"/>
                    <button type="button" className="ob-exit" onClick={leave}>
                        <SignOut size={15}/>
                        {/* Lo que el botón promete depende de si la línea ya existe: antes del paso 4
                            irse no deja nada creado, y decir «guardar» sería mentir; después, la
                            línea está creada y se termina de configurar cuando se quiera. */}
                        <span>{draft.created.businessUnitId ? t('line_wizard.exit_created') : t('line_wizard.exit')}</span>
                    </button>
                </header>
                <main className="ob-body">{children}</main>
            </div>
        </DraftContext.Provider>
    );
}
